<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
    <!-- Navigation Header -->
    <header class="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-20">
          <!-- Logo -->
          <div class="flex items-center">
            <router-link to="/" class="flex items-center space-x-3 group">
              <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform">
                <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy" class="w-6 h-6">
              </div>
              <span class="text-2xl font-bold text-white group-hover:text-emerald-400 transition-colors">
                OilBuddy
              </span>
              <span class="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent text-sm font-semibold px-2 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                AI
              </span>
            </router-link>
          </div>

          <!-- Navigation Links -->
          <nav class="hidden md:flex items-center space-x-8">
            <router-link to="/" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Home</router-link>
            <a href="/#features" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Features</a>
            <a href="/#how-it-works" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">How it Works</a>
            <a href="/#pricing" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Pricing</a>
            <a href="/#testimonials" class="text-slate-300 hover:text-emerald-400 transition-all duration-300 font-medium">Testimonials</a>
            <router-link to="/blog" class="text-emerald-400 hover:text-emerald-300 transition-all duration-300 font-medium">Blog</router-link>
            <a href="/#/login" class="text-slate-300 hover:text-emerald-400 font-semibold transition-all duration-300">Sign In</a>
            <router-link to="/register" class="btn-primary-modern">
              Get Started Free
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </router-link>
          </nav>

          <!-- Mobile Menu Button -->
          <button @click="mobileMenuOpen = !mobileMenuOpen" class="md:hidden p-2 text-slate-300 hover:text-emerald-400 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
        </div>

        <!-- Mobile Menu -->
        <div v-if="mobileMenuOpen" class="md:hidden py-4 border-t border-slate-700/50">
          <div class="space-y-2">
            <router-link to="/" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Home</router-link>
            <a href="/#features" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Features</a>
            <a href="/#how-it-works" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">How it Works</a>
            <a href="/#pricing" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Pricing</a>
            <a href="/#testimonials" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Testimonials</a>
            <router-link to="/blog" class="block px-3 py-3 text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Blog</router-link>
            <a href="/#/login" class="block px-3 py-3 text-slate-300 hover:text-emerald-400 hover:bg-slate-800/50 rounded-lg transition-all">Sign In</a>
            <router-link to="/register" class="block px-3 py-3 btn-primary-modern mt-2">Get Started Free</router-link>
          </div>
        </div>
      </div>
    </header>

    <div class="pt-20 min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
      <!-- Animated Background -->
      <div class="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-900/20 via-transparent to-transparent pointer-events-none"></div>
      <div class="fixed inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>
      
      <!-- Floating Particles -->
      <div class="fixed inset-0 pointer-events-none">
        <div class="particle particle-1"></div>
        <div class="particle particle-2"></div>
        <div class="particle particle-3"></div>
        <div class="particle particle-4"></div>
        <div class="particle particle-5"></div>
      </div>

      <div class="relative z-10 p-8">
        <!-- Page Header -->
        <div class="mb-8">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            Latest Updates
          </div>
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            OilBuddy AI
            <span class="block bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Blog & News
            </span>
          </h1>
          <p class="text-xl text-slate-300 mb-8 max-w-2xl">Stay updated with the latest insights, features, and industry news from the oilfield technology world</p>
        </div>

        <!-- Blog Posts Grid -->
        <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div v-for="n in 6" :key="n" class="bg-white/5 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-6 animate-pulse">
            <div class="h-4 bg-slate-600/50 rounded mb-4"></div>
            <div class="h-6 bg-slate-600/50 rounded mb-3"></div>
            <div class="h-4 bg-slate-600/50 rounded mb-2"></div>
            <div class="h-4 bg-slate-600/50 rounded mb-2"></div>
            <div class="h-4 bg-slate-600/50 rounded w-3/4"></div>
          </div>
        </div>

        <div v-else-if="error" class="text-center py-12">
          <div class="text-red-400 text-lg mb-4">{{ error }}</div>
          <button @click="loadBlogPosts" class="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-xl transition-colors">
            Try Again
          </button>
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Featured Post -->
          <div v-if="featuredPost" @click="navigateToPost(featuredPost.id)" class="md:col-span-2 lg:col-span-3 bg-white/5 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 hover:border-emerald-500/30 transition-all duration-500 group cursor-pointer">
            <div class="flex items-center mb-4">
              <span class="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-sm font-medium">Featured</span>
              <span class="text-slate-400 text-sm ml-4">{{ formatDate(featuredPost.created_at) }}</span>
            </div>
            <h2 class="text-2xl md:text-3xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors">
              {{ featuredPost.title }}
            </h2>
            <p class="text-slate-300 text-lg leading-relaxed mb-6">
              {{ featuredPost.excerpt }}
            </p>
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold mr-3">
                  {{ featuredPost.author.charAt(0) }}
                </div>
                <div>
                  <div class="text-white font-medium">{{ featuredPost.author }}</div>
                  <div class="text-slate-400 text-sm">{{ featuredPost.category }}</div>
                </div>
              </div>
              <div class="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-6 py-2 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105">
                Read More
              </div>
            </div>
          </div>

          <!-- Regular Blog Posts -->
          <div v-for="post in regularPosts" :key="post.id" @click="navigateToPost(post.id)" class="bg-white/5 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-6 hover:border-emerald-500/30 transition-all duration-500 group cursor-pointer">
            <div class="flex items-center mb-4">
              <span :class="getCategoryClass(post.category)" class="px-3 py-1 rounded-full text-sm font-medium">{{ post.category }}</span>
              <span class="text-slate-400 text-sm ml-4">{{ formatDate(post.created_at) }}</span>
            </div>
            <h3 class="text-xl font-bold text-white mb-3 group-hover:text-emerald-400 transition-colors line-clamp-2">
              {{ post.title }}
            </h3>
            <p class="text-slate-300 mb-4 line-clamp-3">
              {{ post.excerpt }}
            </p>
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="w-8 h-8 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-bold text-sm mr-3">
                  {{ post.author.charAt(0) }}
                </div>
                <div class="text-slate-400 text-sm">{{ post.author }}</div>
              </div>
              <svg class="w-5 h-5 text-emerald-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
              </svg>
            </div>
          </div>
        </div>

        <!-- Newsletter Signup -->
        <div class="mt-16 bg-white/5 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 text-center">
          <h3 class="text-2xl font-bold text-white mb-4">Stay in the Loop</h3>
          <p class="text-slate-300 mb-6 max-w-2xl mx-auto">
            Get the latest updates on new features, industry insights, and tips to maximize your oilfield operations directly in your inbox.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email" 
              class="flex-1 px-4 py-3 bg-slate-800/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-400 focus:border-emerald-500 focus:outline-none"
            >
            <button class="bg-gradient-to-r from-emerald-500 to-cyan-500 text-white px-6 py-3 rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 font-semibold">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { blogService, type BlogPost } from '@/services/blogService'

// State
const router = useRouter()
const mobileMenuOpen = ref(false)
const blogPosts = ref<BlogPost[]>([])
const isLoading = ref(true)
const error = ref<string | null>(null)

// Computed properties
const featuredPost = computed(() => {
  return blogPosts.value.find(post => post.featured)
})

const regularPosts = computed(() => {
  return blogPosts.value.filter(post => !post.featured)
})

// Methods
const getCategoryClass = (category: string) => {
  switch (category.toLowerCase()) {
    case 'product':
      return 'bg-emerald-500/20 text-emerald-400'
    case 'industry':
      return 'bg-cyan-500/20 text-cyan-400'
    case 'tips':
      return 'bg-amber-500/20 text-amber-400'
    case 'updates':
      return 'bg-purple-500/20 text-purple-400'
    default:
      return 'bg-slate-500/20 text-slate-400'
  }
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

const navigateToPost = (postId: number) => {
  router.push(`/blog/${postId}`)
}

const loadBlogPosts = async () => {
  try {
    isLoading.value = true
    error.value = null
    const posts = await blogService.getBlogPosts()
    blogPosts.value = posts
  } catch (err) {
    console.error('Error loading blog posts:', err)
    error.value = 'Failed to load blog posts. Please try again later.'
  } finally {
    isLoading.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadBlogPosts()
})
</script>

<style scoped>
/* Background patterns and animations */
.bg-grid-pattern {
  background-image: radial-gradient(circle, rgba(148, 163, 184, 0.3) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: grid-move 20s linear infinite;
}

@keyframes grid-move {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* Floating particles */
.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.particle-1 { top: 20%; left: 10%; animation-delay: 0s; }
.particle-2 { top: 60%; left: 20%; animation-delay: 1s; }
.particle-3 { top: 40%; left: 80%; animation-delay: 2s; }
.particle-4 { top: 80%; left: 70%; animation-delay: 3s; }
.particle-5 { top: 30%; left: 60%; animation-delay: 4s; }

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
  50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
}

/* Line clamping for text truncation */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Header button styles */
.btn-primary-modern {
  @apply inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-2xl hover:from-emerald-400 hover:to-cyan-400 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}
</style>
